import type { MiharuConfig } from '@/types/config'
import type { 
  InstanceContext, 
  MiharuInstance, 
  InstanceStats, 
  InstanceMetrics,
  PatchResult,
  TraceData,
  StorageAdapter
} from '@/types/core'
import { MiharuError } from '@/types/core'
import { Logger } from '@/utils/logger'
import { DynamicBatcher } from '@/memory/dynamic-batcher'
import { MemoryPressureMonitor, DefaultMemoryHandlers } from '@/memory/pressure-monitor'
import type { LLMCallData, ErrorData } from '@/types/core'

const logger = new Logger('MiharuInstance')

export class MiharuInstanceImpl implements MiharuInstance {
  public readonly id: string
  public readonly config: MiharuConfig
  public readonly context: InstanceContext | undefined
  
  private initialized = false
  private disabled = false
  private startTime: number
  private defaultMetadata: Record<string, unknown> = {}
  
  // Getter for external access to default metadata
  public getDefaultMetadata(): Record<string, unknown> {
    return { ...this.defaultMetadata }
  }
  
  // Instance state
  private patchedClients = new WeakSet()
  private activeTraces = new Map<string, TraceData>()
  private storage?: StorageAdapter

  // Memory management
  private llmCallBatcher?: DynamicBatcher<LLMCallData>
  private errorBatcher?: DynamicBatcher<ErrorData>
  private traceBatcher?: DynamicBatcher<TraceData>
  private memoryMonitor?: MemoryPressureMonitor
  
  // Performance tracking
  private stats: InstanceStats = {
    totalCalls: 0,
    totalCost: 0,
    totalTokens: 0,
    batchesProcessed: 0,
    errors: 0,
    uptime: 0
  }

  constructor(
    instanceId: string,
    config: MiharuConfig,
    context?: InstanceContext
  ) {
    this.id = instanceId
    this.config = config
    this.context = context
    this.startTime = Date.now()
    
    this.initializeWithContext()
  }

  private initializeWithContext(): void {
    // Add context to all traces
    if (this.context) {
      this.defaultMetadata = {
        instance_id: this.id,
        environment: this.context.environment,
        service: this.context.service,
        team: this.context.team,
        ...this.context.metadata,
      }
    }
  }

  async init(): Promise<void> {
    if (this.initialized) {
      logger.warn(`Instance ${this.id} already initialized`)
      return
    }

    try {
      // Initialize storage adapter
      await this.initializeStorage()
      
      // Initialize other components based on configuration
      await this.initializeComponents()
      
      this.initialized = true
      logger.info(`Instance ${this.id} initialized successfully`)
    } catch (error) {
      logger.error(`Failed to initialize instance ${this.id}:`, error)
      throw new MiharuError(
        `Instance initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'INITIALIZATION_FAILED',
        error instanceof Error ? error : undefined
      )
    }
  }

  private async initializeStorage(): Promise<void> {
    // Storage adapter initialization will be implemented in Phase 1-2
    logger.debug(`Initializing storage adapter: ${this.config.adapter}`)
    
    // For now, just log the configuration
    switch (this.config.adapter) {
      case 'sqlite':
        logger.debug('SQLite adapter will be initialized')
        break
      case 'supabase':
        logger.debug('Supabase adapter will be initialized')
        break
      case 'memory':
        logger.debug('Memory adapter will be initialized')
        break
    }
  }

  private async initializeComponents(): Promise<void> {
    // Initialize batching, session management, etc.
    logger.debug('Initializing core components')
    
    // Apply configuration defaults
    if (!this.config.batchConfig) {
      this.config.batchConfig = {
        minSize: 10,
        maxSize: 100,
        flushInterval: 5000,
        memoryThresholdMB: 8,
        overflowStrategy: 'flush'
      }
    }

    // Initialize memory management components
    await this.initializeMemoryManagement()
  }

  private async initializeMemoryManagement(): Promise<void> {
    logger.debug('Initializing memory management')

    // Initialize memory pressure monitor
    this.memoryMonitor = new MemoryPressureMonitor({
      thresholdMB: this.config.batchConfig?.memoryThresholdMB || 8,
      warningThresholdMB: (this.config.batchConfig?.memoryThresholdMB || 8) * 1.5,
      criticalThresholdMB: (this.config.batchConfig?.memoryThresholdMB || 8) * 2,
      checkInterval: 10000, // 10 seconds
      enableGC: this.config.featureFlags?.enableAutoRecovery ?? true,
      autoFlush: true,
      adaptiveSizing: true
    })

    // Add default memory handlers
    this.memoryMonitor.addHandler(DefaultMemoryHandlers.forceGC)
    this.memoryMonitor.addHandler(DefaultMemoryHandlers.logStats)

    // Add custom flush handler for batchers
    this.memoryMonitor.addHandler({
      priority: 80,
      name: 'flush_batchers',
      handler: async (event) => {
        if (event.level === 'warning' || event.level === 'critical') {
          await this.flushAllBatchers()
          return true
        }
        return false
      }
    })

    // Start monitoring
    this.memoryMonitor.start()

    // Initialize batchers
    const batchConfig = this.config.batchConfig!

    this.llmCallBatcher = new DynamicBatcher<LLMCallData>({
      minSize: batchConfig.minSize,
      maxSize: batchConfig.maxSize,
      flushInterval: batchConfig.flushInterval,
      memoryThresholdMB: batchConfig.memoryThresholdMB,
      overflowStrategy: batchConfig.overflowStrategy,
      enableMemoryMonitoring: true,
      adaptiveSizing: true
    })

    this.errorBatcher = new DynamicBatcher<ErrorData>({
      minSize: Math.max(1, Math.floor(batchConfig.minSize / 2)),
      maxSize: Math.max(10, Math.floor(batchConfig.maxSize / 2)),
      flushInterval: batchConfig.flushInterval,
      memoryThresholdMB: batchConfig.memoryThresholdMB,
      overflowStrategy: batchConfig.overflowStrategy,
      enableMemoryMonitoring: true,
      adaptiveSizing: true
    })

    this.traceBatcher = new DynamicBatcher<TraceData>({
      minSize: batchConfig.minSize,
      maxSize: batchConfig.maxSize,
      flushInterval: batchConfig.flushInterval * 2, // Traces less frequent
      memoryThresholdMB: batchConfig.memoryThresholdMB,
      overflowStrategy: batchConfig.overflowStrategy,
      enableMemoryMonitoring: true,
      adaptiveSizing: true
    })

    // Set up flush handlers
    this.llmCallBatcher.onFlush(async (calls) => {
      if (this.storage) {
        await Promise.all(calls.map(call => this.storage!.saveLLMCall(call)))
      }
      logger.debug(`Flushed ${calls.length} LLM calls`)
    })

    this.errorBatcher.onFlush(async (errors) => {
      if (this.storage) {
        await Promise.all(errors.map(error => this.storage!.saveError(error)))
      }
      logger.debug(`Flushed ${errors.length} errors`)
    })

    this.traceBatcher.onFlush(async (traces) => {
      if (this.storage) {
        await Promise.all(traces.map(trace => this.storage!.saveTrace(trace)))
      }
      logger.debug(`Flushed ${traces.length} traces`)
    })

    logger.info('Memory management initialized')
  }

  async shutdown(): Promise<void> {
    if (!this.initialized) {
      logger.warn(`Instance ${this.id} not initialized, skipping shutdown`)
      return
    }

    try {
      logger.info(`Shutting down instance ${this.id}`)
      
      // Flush any pending data
      await this.flushPendingData()
      
      // Close storage connections
      if (this.storage) {
        await this.storage.shutdown()
      }
      
      // Clean up resources
      this.activeTraces.clear()
      
      this.initialized = false
      logger.info(`Instance ${this.id} shut down successfully`)
    } catch (error) {
      logger.error(`Error during shutdown of instance ${this.id}:`, error)
      throw new MiharuError(
        `Shutdown failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'SHUTDOWN_FAILED',
        error instanceof Error ? error : undefined
      )
    }
  }

  disable(): void {
    this.disabled = true
    logger.info(`Instance ${this.id} disabled`)
  }

  async patchProvider(provider: string, client: unknown): Promise<PatchResult> {
    if (this.disabled) {
      throw new MiharuError('Instance is disabled', 'INSTANCE_DISABLED')
    }

    if (!this.initialized) {
      throw new MiharuError('Instance not initialized', 'INSTANCE_NOT_INITIALIZED')
    }

    try {
      logger.debug(`Patching provider ${provider} for instance ${this.id}`)
      
      let result: PatchResult

      switch (provider.toLowerCase()) {
        case 'openai':
          result = await this.patchOpenAIProvider(client)
          break
        case 'anthropic':
          result = await this.patchAnthropicProvider(client)
          break
        case 'google':
          result = await this.patchGoogleProvider(client)
          break
        default:
          throw new MiharuError(`Unsupported provider: ${provider}`, 'UNSUPPORTED_PROVIDER')
      }

      if (result.success && typeof client === 'object' && client !== null) {
        this.patchedClients.add(client)
        logger.info(`Successfully patched ${provider} for instance ${this.id}`)
      }

      return result
    } catch (error) {
      logger.error(`Failed to patch provider ${provider}:`, error)
      return {
        success: false,
        method: 'wrapper',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  private async patchOpenAIProvider(client: unknown): Promise<PatchResult> {
    try {
      const { openaiPatcher } = await import('@/providers/openai/patcher')
      return await openaiPatcher.patchClient(client as any, this)
    } catch (error) {
      logger.error('OpenAI patching failed', error)
      return {
        success: false,
        method: 'openai-patch',
        error: error instanceof Error ? error.message : 'OpenAI patch error'
      }
    }
  }

  private async patchAnthropicProvider(_client: unknown): Promise<PatchResult> {
    // Anthropic patching will be implemented in later phases
    logger.warn('Anthropic provider patching not yet implemented')
    return {
      success: false,
      method: 'anthropic-patch',
      error: 'Anthropic patching not implemented'
    }
  }

  private async patchGoogleProvider(_client: unknown): Promise<PatchResult> {
    // Google provider patching will be implemented in later phases
    logger.warn('Google provider patching not yet implemented')
    return {
      success: false,
      method: 'google-patch',
      error: 'Google patching not implemented'
    }
  }

  async getStats(): Promise<InstanceStats> {
    this.stats.uptime = Date.now() - this.startTime
    return { ...this.stats }
  }

  getMetrics(): InstanceMetrics {
    return {
      instanceId: this.id,
      context: this.context,
      activeTraces: this.activeTraces.size,
      patchedClients: this.getPatchedClientCount(),
      memoryUsage: process.memoryUsage(),
      uptime: Date.now() - this.startTime,
    }
  }

  private getPatchedClientCount(): number {
    // WeakSet doesn't have a size property, so we'll track this differently
    // For now, return a placeholder
    return 0
  }

  private async flushPendingData(): Promise<void> {
    logger.debug(`Flushing pending data for instance ${this.id}`)
    
    // Flush all batchers if they exist
    await this.flushAllBatchers()
    
    // Stop memory monitoring
    if (this.memoryMonitor) {
      this.memoryMonitor.stop()
      this.memoryMonitor = undefined
    }
    
    // Shutdown batchers
    if (this.llmCallBatcher) {
      await this.llmCallBatcher.shutdown()
      this.llmCallBatcher = undefined
    }
    
    if (this.errorBatcher) {
      await this.errorBatcher.shutdown()
      this.errorBatcher = undefined
    }
    
    if (this.traceBatcher) {
      await this.traceBatcher.shutdown()
      this.traceBatcher = undefined
    }
  }

  // Internal methods for updating stats
  public incrementCallCount(): void {
    this.stats.totalCalls++
  }

  public addCost(cost: number): void {
    this.stats.totalCost += cost
  }

  public addTokens(tokens: number): void {
    this.stats.totalTokens += tokens
  }

  public incrementErrors(): void {
    this.stats.errors++
  }

  public incrementBatchesProcessed(): void {
    this.stats.batchesProcessed++
  }

  // Trace management
  public addTrace(trace: TraceData): void {
    this.activeTraces.set(trace.id, trace)
  }

  public removeTrace(traceId: string): void {
    this.activeTraces.delete(traceId)
  }

  public getTrace(traceId: string): TraceData | undefined {
    return this.activeTraces.get(traceId)
  }

  // Memory management methods
  private async flushAllBatchers(): Promise<void> {
    const flushPromises: Promise<any>[] = []
    
    if (this.llmCallBatcher) {
      flushPromises.push(this.llmCallBatcher.flush())
    }
    
    if (this.errorBatcher) {
      flushPromises.push(this.errorBatcher.flush())
    }
    
    if (this.traceBatcher) {
      flushPromises.push(this.traceBatcher.flush())
    }
    
    if (flushPromises.length > 0) {
      await Promise.all(flushPromises)
      logger.debug('All batchers flushed successfully')
    }
  }

  // Public methods for storing data through batchers
  public async storeLLMCall(callData: LLMCallData): Promise<void> {
    if (this.llmCallBatcher) {
      await this.llmCallBatcher.add(callData)
    } else {
      // Fallback to direct storage
      if (this.storage) {
        await this.storage.saveLLMCall(callData)
      }
    }
  }

  public async storeError(errorData: ErrorData): Promise<void> {
    if (this.errorBatcher) {
      await this.errorBatcher.add(errorData)
    } else {
      // Fallback to direct storage
      if (this.storage) {
        await this.storage.saveError(errorData)
      }
    }
  }

  public async storeTrace(traceData: TraceData): Promise<void> {
    if (this.traceBatcher) {
      await this.traceBatcher.add(traceData)
    } else {
      // Fallback to direct storage
      if (this.storage) {
        await this.storage.saveTrace(traceData)
      }
    }
  }

  // Memory monitoring methods
  public getMemoryStatus(): {
    heapUsedMB: number
    isUnderPressure: boolean
    batcherSizes: {
      llmCalls: number
      errors: number
      traces: number
    }
  } | null {
    if (!this.memoryMonitor) {
      return null
    }

    const status = this.memoryMonitor.getCurrentStatus()
    
    return {
      heapUsedMB: status.heapUsedMB,
      isUnderPressure: status.isUnderPressure,
      batcherSizes: {
        llmCalls: this.llmCallBatcher?.getStatus().bufferSize || 0,
        errors: this.errorBatcher?.getStatus().bufferSize || 0,
        traces: this.traceBatcher?.getStatus().bufferSize || 0
      }
    }
  }

  public async forceFlush(): Promise<void> {
    await this.flushAllBatchers()
  }
}